import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartItemListBlockConfig } from '@front-platform/content-builder';
import { cartItemListBlockResolveConfig } from '@front-platform/content-builder';
import { EMPTY, of } from 'rxjs';
import { Component, Inject, OnInit, input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import {
  CartItemKeys,
  ReconstructedCartItemKeys,
  ReconstructedCartKeys,
} from '@config/keys/cart.keys';
import { ProductKeys } from '@config/keys/product.keys';
import { DistributionModeKeys, ShopKeys } from '@config/keys/shop.keys';

import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { BasketStoreDispatcher } from '@common/dispatchers/basket.dispatcher';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { BasketStoreRefiner } from '@common/refiners/basket.refiner';
import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';

import { CartService } from '@core/services/cart/cart.service';
import { ShopsService } from '@core/services/shops/shops.service';
import { BasketService } from '@core/services/basket/basket.service';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { ModalsService } from '@core/services/modals/modals.service';
import { LinkNavigationService } from '@core/services/link-navigation/link-navigation.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';

import { GroupedOrderModalsAttributes } from '@config/modals/grouped-order-modals.config';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { DeliveryDistributionModes } from '@config/values/order.values';
import { ICoupon, DoodReconstructedCart, DoodReconstructedCartItem } from '@core/models/cart.model';
import {
  ShareGroupedOrderModalComponent,
  ShareGroupedOrderModalConfig,
} from '@shared/modals/share-grouped-order-modal/share-grouped-order-modal.component';
import {
  ConfirmModalComponent,
  ConfirmModalConfig,
} from '@shared/modals/confirm-modal/confirm-modal.component';
import { UntypedModalIO } from '@core/services/modals/modals-util.type';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { PrepaidAccountTopUpModalConfig } from '@shared/modals/prepayment-modal/prepaid-account-top-up-modal.component';
import {
  ItemEditModalComponent,
  type ItemEditModalConfig,
} from '@shared/modals/item-edit-modal/item-edit-modal.component';
import { DoodProductModel } from '@core/models/product.model';
import { PrepaymentSelectorService } from '@common/selectors/prepayment.selector';

import {
  CART_ITEM_LIST_BLOCK_DATA_SOURCE,
  CartItemListBlockDataSource,
} from './cart-item-list-block-view-model';
import { provideCartItemListBlockDataSource } from './cart-item-list-block-data-source.providers';

const DEFAULT_CLEAR_CART_FALLBACK_LINK = '/{{activeShop}}';
export interface CartItemListBlockRuntimeConfig {}

export type CartItemListBlockConfigInput = Partial<
  Omit<CartItemListBlockConfig, keyof CartItemListBlockRuntimeConfig> &
    CartItemListBlockRuntimeConfig
>;
export type CartItemListBlockResolvedConfig = Omit<
  CartItemListBlockConfig,
  keyof CartItemListBlockRuntimeConfig
> &
  Partial<CartItemListBlockRuntimeConfig>;
export interface CartItemListBlockContract extends ConfigComponentContract<
  CartItemListBlockConfigInput,
  CartItemListBlockResolvedConfig
> {}

@Component({
  selector: 'app-cart-item-list-block',
  templateUrl: './cart-item-list-block.component.html',
  standalone: false,
  providers: [...provideCartItemListBlockDataSource()],
})
export class CartItemListBlockComponent
  extends DestroyerBase
  implements OnInit, CartItemListBlockContract
{
  readonly config = input<CartItemListBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartItemListBlockConfig,
    CartItemListBlockRuntimeConfig
  >(cartItemListBlockResolveConfig, () => ({ config: this.config() }));
  get headingColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor;
  }
  get textColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor;
  }
  get secondaryTextColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['secondary_text_color'] as unknown as IContentBuilderFieldColor;
  }
  get productNameColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['product_name_color'] as unknown as IContentBuilderFieldColor;
  }
  get productDetailsColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['product_details_color'] as unknown as IContentBuilderFieldColor;
  }
  get quantityBadgeTextColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['quantity_badge_text_color'] as unknown as IContentBuilderFieldColor;
  }
  get quantityBadgeBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['quantity_badge_background_color'] as unknown as IContentBuilderFieldColor;
  }
  get clearCartRedirectLink(): string {
    return this.resolvedConfig()['clear_cart_redirect_link'] as unknown as string;
  }

  isGroupedOrderAllowed?: boolean;

  userId$ = this.authSelector.selectUserId;

  protected readonly cartListVm$;

  basket$ = this.basketSelector.select;
  basketId$ = this.basketSelector.select.pipe(map(basket => basket[CartItemKeys.Id]));

  reconstructedShareOrderBasket$ = this.basketRefiner.selectCartItemsGroupedByUser;

  shopKeys = ShopKeys;
  productKeys = ProductKeys;
  cartItemKeys = CartItemKeys;
  reconstructedCartKeys = ReconstructedCartKeys;
  reconstructedCartItemKeys = ReconstructedCartItemKeys;

  constructor(
    private shopSelector: ShopStoreSelector,
    private cartSelector: CartStoreSelector,
    private authSelector: AuthStoreSelector,
    private basketRefiner: BasketStoreRefiner,
    private readonly cartService: CartService,
    private basketSelector: BasketStoreSelector,
    private readonly shopsService: ShopsService,
    private readonly modalsService: ModalsService<
      [
        PrepaidAccountTopUpModalConfig,
        ShareGroupedOrderModalConfig,
        ConfirmModalConfig,
        ItemEditModalConfig,
      ]
    >,
    private readonly translateService: TranslateService,
    private readonly basketService: BasketService,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private basketDispatcher: BasketStoreDispatcher,
    private readonly cartDispatcher: CartStoreDispatcher,
    private readonly analyticsService: AnalyticsService,
    private readonly prepaymentSelectorService: PrepaymentSelectorService,
    private readonly navigationHistoryService: NavigationHistoryService,
    private readonly linkNavigationService: LinkNavigationService,
    @Inject(CART_ITEM_LIST_BLOCK_DATA_SOURCE)
    blockDataSource: CartItemListBlockDataSource,
  ) {
    super();
    this.cartListVm$ = blockDataSource.vm$;
  }

  ngOnInit(): void {
    this.cartService.loadShopsOfCart$().subscribe();
    this.setIsGroupOrderAllowed();
  }

  onRemoveCartItem(
    cartItemId: string | number,
    cart: DoodReconstructedCart,
    reconstructedCartArray: DoodReconstructedCart[],
  ): void {
    if (cart[ReconstructedCartKeys.Items].length === 1 && reconstructedCartArray.length === 1) {
      this.cartService.clearCart();
      this.navigateAfterClearCart();
      return;
    }

    this.cartService.removeItem$(cartItemId as number).subscribe();
  }

  onRemoveBasketItem(itemId: string | number): void {
    this.basketSelector.select
      .pipe(
        take(1),
        switchMap(basket => {
          if (basket.id) {
            return this.basketService.removeBasketItem$(itemId as string);
          } else {
            return this.cartService.removeItem$(itemId as number);
          }
        }),
      )
      .subscribe();
  }

  onUpdateItem(
    cartItemId: string | number,
    product?: DoodProductModel,
    groupOrderItemId?: string,
  ): void {
    const topUpProductId = this.prepaymentSelectorService.selectTopUpProductId();
    if (product && topUpProductId && topUpProductId === product.id) {
      this.modalsService.open('prepaid-account-top-up-modal', undefined, {
        shopId: product.shop_id,
      });
      return;
    }
    if (groupOrderItemId) {
      this.basketDispatcher.updateBasket({ itemEditId: groupOrderItemId });
    }
    this.openItemEditModal(cartItemId);
  }

  private openItemEditModal(itemId: string | number): void {
    this.cartService.loadShopsOfCart$().pipe(takeUntil(this._destroyerRef)).subscribe();
    this.cartService
      .getCartLineItems$()
      .pipe(
        takeUntil(this._destroyerRef),
        take(1),
        map(reconstructedCart => this.lookForItemInCart(itemId, reconstructedCart)),
        tap(cartItem => {
          if (!cartItem) {
            return;
          }
          this.cartDispatcher.updateStore({ edit_item: cartItem });
          this.modalsService.open(ItemEditModalComponent.handle);
        }),
      )
      .subscribe();
  }

  private lookForItemInCart(
    itemId: string | number,
    reconstructedCart: DoodReconstructedCart[],
  ): DoodReconstructedCartItem | null {
    for (const cartOfShop of reconstructedCart) {
      const cartItem = cartOfShop[ReconstructedCartKeys.Items].find(
        elem => elem[ReconstructedCartItemKeys.Item][CartItemKeys.Id] === itemId,
      );
      if (cartItem) {
        return cartItem;
      }
    }

    return null;
  }

  removeUserFromCart(id: string): void {
    this.basketDispatcher.updateBasket({ removeUserId: id });
    this.modalsService.open(
      ShareGroupedOrderModalComponent.handle,
      GroupedOrderModalsAttributes.RemoveUserOrder,
    );
  }

  openGroupOrderModal(): void {
    const basket = this.basketSelector.basket;
    if (basket.id) {
      this.modalsService.open(
        ShareGroupedOrderModalComponent.handle,
        GroupedOrderModalsAttributes.GeneratedLink,
      );
      return;
    }
    this.modalsService.open(
      ShareGroupedOrderModalComponent.handle,
      GroupedOrderModalsAttributes.ShareLink,
    );
  }

  onRemoveCoupon(coupon: ICoupon): void {
    coupon.removeInProgress = true;
    this.basketService
      .removeBasketCoupon$(coupon)
      .pipe(switchMap(() => this.orderDispatcher.checkCartIsValid$()))
      .subscribe(() => (coupon.removeInProgress = false));
  }

  setIsGroupOrderAllowed(): void {
    // TODO: Add support for multi shop baskets
    this.shopSelector.selectActive$
      .pipe(
        switchMap(shop => {
          if (shop?.id) {
            return of(shop);
          }

          const shopId = this.cartSelector.active?.shop;
          if (shopId) {
            return this.shopsService.loadShopById$(shopId);
          }

          return of(undefined);
        }),
        takeUntil(this._destroyerRef),
        tap(shop => {
          const distributionModeFound = shop?.[ShopKeys.DistributionModes].find(
            distributionMode =>
              DeliveryDistributionModes.includes(distributionMode.type) ||
              distributionMode.type === this.cartSelector.active?.type,
          );

          this.isGroupedOrderAllowed =
            distributionModeFound?.[DistributionModeKeys.AllowGroupedOrder] || false;
        }),
      )
      .subscribe();
  }

  clearCart(): void {
    this.modalsService.open(ConfirmModalComponent.handle, undefined, {
      title: this.translateService.instant('basket.clear-basket-confirm-modal.title'),
      message: this.translateService.instant('basket.clear-basket-confirm-modal.message'),
      confirmButtonText: this.translateService.instant('basket.clear-basket-confirm-modal.confirm'),
    });

    this.modalsService
      .event$<ConfirmModalConfig>(ConfirmModalComponent.handle)
      .pipe(
        filter(event => event.type === 'confirm'),
        switchMap(() => this.basketId$),
        take(1),
        switchMap(id => {
          if (id) {
            this.modalsService.open<UntypedModalIO>(
              ShareGroupedOrderModalComponent.handle,
              GroupedOrderModalsAttributes.ClearCart,
            );
            return EMPTY;
          }
          this.cartService.clearCart();
          this.navigateAfterClearCart();
          this.modalsService.close(ConfirmModalComponent.handle);
          return EMPTY;
        }),
        takeUntil(this._destroyerRef),
      )
      .subscribe();
  }

  private navigateAfterClearCart(): void {
    const redirectLink = this.clearCartRedirectLink?.trim();
    if (redirectLink) {
      this.linkNavigationService.navigate(redirectLink);
      return;
    }

    if (this.navigationHistoryService.canGoBack()) {
      this.navigationHistoryService.goBack();
      return;
    }

    this.linkNavigationService.navigate(this.getDefaultClearCartFallbackLink());
  }

  private getDefaultClearCartFallbackLink(): string {
    return this.shopSelector.selectActive() ? DEFAULT_CLEAR_CART_FALLBACK_LINK : '/';
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartItemListBlockComponent, { 'keyAliases': {} });
